import type { SimilarTest } from '@@/api/learnings/similar/similar-types';
import type { ProgramOutcome } from '@@/api/program/classify-outcome';

const OUTCOME_WORD: Record<ProgramOutcome, string> = {
  won: 'won',
  lost: 'lost',
  mixed: 'had mixed results',
  inconclusive: 'was inconclusive',
  'no-data': 'recorded no data',
};

const MONTH = new Intl.DateTimeFormat('en', { month: 'long' });
const MONTH_YEAR = new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' });

type WhenParams = { at: number; now: number };

/** "March" for this year, "March 2025" for any other, so a year-old test is not read as recent. */
const when = ({ at, now }: WhenParams): string => {
  const date = new Date(at);
  return date.getFullYear() === new Date(now).getFullYear() ? MONTH.format(date) : MONTH_YEAR.format(date);
};

/** "-2.8%", "+4.1%": one decimal and an ordinary hyphen, from the percent the server computed. */
const signedPercent = (lift: number): string => `${lift > 0 ? '+' : ''}${lift.toFixed(1)}%`;

/** Where it ran, from why it matched: the same page, a page of the same kind, or only the idea. */
const where = ({ samePage, samePageType }: SimilarTest['reason']): string =>
  samePage ? 'on this page' : samePageType ? 'on a similar page' : 'on another page';

type SimilarSentenceParams = { test: SimilarTest; now: number };

/**
 * One line about a past or current test, after its id: "tested this in March: lost, -2.8%."
 *
 * The outcome and lift were read by the server as the Library reads them, so a win that exists only
 * because a variant was deployed says "won (deployed)", as the Library's chip does: the word is the
 * Library's, the statistics did not call it, and the lift beside it is the statistics'. A test with no dated
 * conclusion is not given a month, and one with no outcome says only that it ran. Running tests
 * and drafts say where they are, never a result. No em dashes.
 */
export function similarSentence({ test, now }: SimilarSentenceParams): string {
  const place = where(test.reason);

  if (test.status === 'Running') return `is running ${place} now.`;
  if (test.status === 'Draft') return `is a draft ${place}.`;
  if (test.status === 'QA') return `is a draft in QA ${place}.`;

  const month = test.concludedAt === null ? '' : ` in ${when({ at: test.concludedAt, now })}`;
  if (test.outcome === null) return `tested this${month}.`;
  if (test.outcome === 'no-data') return `tested this${month}, but recorded no data.`;

  const lift = test.lift === null ? '' : `, ${signedPercent(test.lift)}`;
  const word = test.outcome === 'won' && test.byDeployment ? 'won (deployed)' : OUTCOME_WORD[test.outcome];
  return test.outcome === 'won' || test.outcome === 'lost'
    ? `tested this${month}: ${word}${lift}.`
    : `tested this${month}: ${word}.`;
}
